import { formatClassCount, formatValidityPeriod } from '@/helpers/credits';
import { isSinglePayment } from '@/helpers/subscription';
import {
  membershipGuestPassTile,
  packGuestPassTile,
  type GuestPassRules,
} from '@/helpers/guest-pass';
import type { FactTile } from '@/lib/types';
import {
  formatCycles,
  formatInterval,
  formatIntervalForTerms,
  formatPeriodLength,
  formatPrice,
  formatRecurringPrice,
  getIntervalLabel,
} from '@/lib/utils';
import type { AppearanceSettings } from '@/state/appearance-settings';
import type { ClassPack } from '@/state/class-pack-groups';
import type { Subscription } from '@/state/subscription-groups';

// The pills under the title and the "Good to know" tiles. Every one comes from
// a field the studio sets, and shows only when that field is set.
export type OfferingFacts = { pills: string[]; tiles: FactTile[] };

// No-break spaces, so a tile breaks before these words and never among them.
const FROM_PURCHASE_DATE = ' from\u00a0purchase\u00a0date';

const isPill = (pill: string | false | null): pill is string => !!pill;
const isTile = (tile: FactTile | false): tile is FactTile => !!tile;

const purchaseLimitTiles = (
  offering: Pick<ClassPack, 'new_customers_only' | 'one_time_purchase_only'>
): (FactTile | false)[] => [
  offering.new_customers_only && {
    key: 'new_customers',
    illustration: 'new-customer-3d',
    lead: 'For ',
    value: 'new customers',
    tail: ' only',
  },
  offering.one_time_purchase_only && {
    key: 'once',
    illustration: 'lock-3d',
    lead: 'Can be bought ',
    value: 'once',
  },
];

const guestPassRules = (
  offering: Pick<ClassPack, 'guest_pass_new_guests_only'>,
  studioName: string | undefined
): GuestPassRules => ({
  newGuestsOnly: !!offering.guest_pass_new_guests_only,
  studioName,
});

export const getClassPackFacts = (
  classPack: ClassPack,
  settings: AppearanceSettings['class_pack'],
  studioName?: string
): OfferingFacts => {
  const pills = [
    settings.show_price && formatPrice(classPack.price, classPack.currency),
    classPack.total_number_of_classes != null &&
      formatClassCount(classPack.total_number_of_classes),
  ];
  // Validity runs from payment; no days means the pack never expires.
  const tiles: (FactTile | false)[] = [
    settings.show_validity_period && {
      key: 'validity',
      illustration: 'waitlist-hourglass-3d',
      ...(classPack.expiry_days
        ? {
            lead: 'Valid ',
            value: formatValidityPeriod(classPack.expiry_days),
            tail: FROM_PURCHASE_DATE,
          }
        : { lead: 'Classes ', value: 'never expire' }),
    },
    packGuestPassTile(
      classPack.guest_passes,
      guestPassRules(classPack, studioName)
    ),
    ...purchaseLimitTiles(classPack),
  ];

  return { pills: pills.filter(isPill), tiles: tiles.filter(isTile) };
};

const subscriptionClassesPill = (subscription: Subscription) => {
  const classes = subscription.total_number_of_classes_per_cycle;
  if (!subscription.charge_every_interval || classes === 0) return null;
  if (classes == null) return 'Unlimited classes';
  if (
    subscription.renewal_classes_behavior === 'LEAVE_AS_IS' ||
    isSinglePayment(subscription)
  ) {
    return formatClassCount(classes);
  }
  return `${formatClassCount(classes)}${formatInterval(
    subscription.charge_every_count || 1,
    subscription.charge_every_interval
  )}`;
};

// Unlimited plans have no credits to expire or roll over, so no tile.
const subscriptionCreditsTile = (
  subscription: Subscription
): FactTile | false => {
  const classes = subscription.total_number_of_classes_per_cycle;
  if (!classes) return false;
  if (subscription.renewal_classes_behavior === 'LEAVE_AS_IS') {
    return {
      key: 'credits',
      illustration: 'class-credits',
      lead: 'All ',
      value: formatClassCount(classes),
      tail: ' upfront, none added when it renews',
    };
  }
  if (subscription.renewal_classes_behavior === 'RESET') {
    const period =
      (subscription.charge_every_count || 1) === 1
        ? getIntervalLabel(subscription.charge_every_interval)
        : 'billing period';
    return {
      key: 'credits',
      illustration: 'class-credits',
      lead: 'Unused classes ',
      value: 'expire',
      tail: ` each ${period}`,
    };
  }
  return {
    key: 'credits',
    illustration: 'class-credits',
    lead: 'Unused classes ',
    value: 'roll over',
    tail: " while you're a member",
  };
};

// One payment reads like a pack: a price and how long it lasts, since the
// backend ends it with its first period and no credit is ever added.
const singlePaymentFacts = (
  subscription: Subscription,
  settings: AppearanceSettings['subscription'],
  studioName: string | undefined
): OfferingFacts => {
  const pills = [
    settings.show_price &&
      formatPrice(subscription.price, subscription.currency),
    subscriptionClassesPill(subscription),
  ];
  const tiles: (FactTile | false)[] = [
    !!subscription.charge_every_interval && {
      key: 'validity',
      illustration: 'waitlist-hourglass-3d',
      lead: 'Valid ',
      value: formatPeriodLength(
        subscription.charge_every_count || 1,
        subscription.charge_every_interval
      ),
      tail: FROM_PURCHASE_DATE,
    },
    membershipGuestPassTile(
      subscription,
      guestPassRules(subscription, studioName)
    ),
    ...purchaseLimitTiles(subscription),
  ];

  return { pills: pills.filter(isPill), tiles: tiles.filter(isTile) };
};

export const getSubscriptionFacts = (
  subscription: Subscription,
  settings: AppearanceSettings['subscription'],
  studioName?: string
): OfferingFacts => {
  if (isSinglePayment(subscription)) {
    return singlePaymentFacts(subscription, settings, studioName);
  }
  const cycles = subscription.number_of_billing_cycles;
  const pills = [
    settings.show_price &&
      formatRecurringPrice(
        subscription.price,
        subscription.currency,
        subscription.charge_every_count,
        subscription.charge_every_interval
      ),
    subscriptionClassesPill(subscription),
  ];
  const tiles: (FactTile | false)[] = [
    !!subscription.charge_every_interval && {
      key: 'renewal',
      illustration: 'subscription-3d',
      ...(cycles && cycles > 0
        ? {
            lead: 'Ends after ',
            value: formatCycles(cycles),
          }
        : {
            lead: 'Renews ',
            value: formatIntervalForTerms(
              subscription.charge_every_count || 1,
              subscription.charge_every_interval
            ),
          }),
    },
    subscriptionCreditsTile(subscription),
    membershipGuestPassTile(
      subscription,
      guestPassRules(subscription, studioName)
    ),
    ...purchaseLimitTiles(subscription),
  ];

  return { pills: pills.filter(isPill), tiles: tiles.filter(isTile) };
};
